import { t } from "@/lib/i18n";
import { useState } from "react";
import { MessageCircle, Trash2, CalendarPlus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { STATUSES, statusInfo, caseWhatsAppUrl, formatAppt } from "@/lib/cases";
import { AppointmentDialog, CalendarActions } from "./AppointmentDialog";

const Appointment = ({ c, onOpen }) =>
  c.appointment ? (
    <div data-testid={`case-appointment-${c.folio}`} className="mt-3 space-y-2 rounded-xl border border-ac-accent-line bg-ac-accent-soft p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-ac-accent-ink"><CalendarClock className="h-4 w-4" /> {formatAppt(c.appointment.at)}</p>
        <button data-testid={`case-appointment-edit-${c.folio}`} onClick={onOpen} className="text-xs font-medium text-ac-accent-ink underline-offset-2 hover:underline">{t("case.change")}</button>
      </div>
      {c.appointment.place && <p className="text-xs text-ac-accent-ink">{c.appointment.place}</p>}
      <CalendarActions c={c} />
    </div>
  ) : (
    <button data-testid={`case-appointment-open-${c.folio}`} onClick={onOpen} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-ac-accent px-3 py-2.5 text-sm font-semibold text-ac-accent-ink transition-colors hover:bg-ac-accent-soft">
      <CalendarPlus className="h-4 w-4" /> {t("case.schedule")}
    </button>
  );

export const CaseCard = ({ c, onUpdate, onForget, onSetAppointment }) => {
  const [justChanged, setJustChanged] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);
  const idx = STATUSES.findIndex((s) => s.key === c.status);
  const s = statusInfo(c.status);

  const saveAppt = async (folio, payload) => {
    const r = await onSetAppointment(folio, payload);
    setJustChanged(true);
    return r;
  };

  const change = async (key) => {
    if (key === c.status) return;
    if (key === "cita") return setApptOpen(true);
    try {
      await onUpdate(c.folio, key);
      setJustChanged(true);
      toast.success(t("case.updated", { s: statusInfo(key).label }));
    } catch {
      toast.error(t("case.updErr"));
    }
  };

  return (
    <article data-testid={`case-card-${c.folio}`} className="rounded-2xl border border-ac-line bg-ac-surface p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-mono text-xs font-semibold text-ac-accent-ink">{c.folio}</p>
          <p className="mt-0.5 line-clamp-2 text-sm font-semibold text-ac-ink">{c.title}</p>
        </div>
        <button data-testid={`case-forget-${c.folio}`} onClick={() => onForget(c.folio)} className="text-slate-300 hover:text-red-500" aria-label={t("case.remove")}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-4 flex items-center">
        {STATUSES.map((st, i) => (
          <div key={st.key} className="flex flex-1 items-center last:flex-none">
            <button
              data-testid={`case-status-${c.folio}-${st.key}`}
              title={st.label}
              onClick={() => change(st.key)}
              className={`grid h-8 w-8 shrink-0 place-items-center rounded-full border-2 transition-colors ${
                i <= idx ? "border-ac-primary bg-ac-primary text-white" : "border-ac-line bg-ac-surface text-ac-faint hover:border-ac-primary"
              }`}
            >
              <st.Icon className="h-3.5 w-3.5" />
            </button>
            {i < STATUSES.length - 1 && <span className={`h-0.5 flex-1 ${i < idx ? "bg-ac-primary" : "bg-ac-line"}`} />}
          </div>
        ))}
      </div>
      <p data-testid={`case-status-label-${c.folio}`} className="mt-3 text-sm"><b className="text-ac-primary-ink">{s.label}</b> <span className="text-ac-muted">· {s.next}</span></p>
      <Appointment c={c} onOpen={() => setApptOpen(true)} />
      <AppointmentDialog key={`${c.folio}-${apptOpen}`} c={c} open={apptOpen} onClose={() => setApptOpen(false)} onSave={saveAppt} />
      <a
        data-testid={`case-notify-${c.folio}`}
        href={caseWhatsAppUrl(c, "update")}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setJustChanged(false)}
        className={`mt-3 flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
          justChanged ? "hg-glow bg-[#25D366] text-white" : "border border-ac-line text-[#128C4A] hover:bg-ac-primary-soft"
        }`}
      >
        <MessageCircle className="h-4 w-4" /> {justChanged ? t("case.notifyNew") : t("case.notify")}
      </a>
    </article>
  );
};
